import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { AxiosError } from 'axios'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AuthLayout } from '@/features/auth/auth-layout'
import { UserAuthForm } from '@/features/auth/sign-in/components/user-auth-form'
import { WeChatAuthErrorAlert } from '@/features/auth/wechat/WeChatAuthErrorAlert'
import { WeChatQrLoginButton } from '@/features/auth/wechat/WeChatQrLoginButton'
import { isWeChatAuthEnabled } from '@/features/auth/wechat/isWeChatAuthEnabled'

export type SignInProps = {
  onSignIn: (payload: { email: string; password: string }) => Promise<void>
  isSigningIn: boolean
  /** When false, hides the sign-up prompt. Defaults to true. */
  showSignUpLink?: boolean
  /** Optional branding above the card (Lex `auth` locale or caller-provided copy). */
  authLayoutBadge?: string
  authLayoutSubtitle?: string
  /** When true, auth layout omits the language switcher. */
  hideInterfaceLanguageSelect?: boolean
}

export function SignIn({
  onSignIn,
  isSigningIn,
  showSignUpLink = true,
  authLayoutBadge,
  authLayoutSubtitle,
  hideInterfaceLanguageSelect = false,
}: SignInProps) {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const [serverError, setServerError] = useState<string | null>(null)
  const wechatError = searchParams.get('wechatError')
  const showWeChatAuth = isWeChatAuthEnabled()

  const signUpPath = useMemo(() => {
    const redirect = searchParams.get('redirect')
    if (redirect) {
      return `/signup?redirect=${encodeURIComponent(redirect)}`
    }
    return '/signup'
  }, [searchParams])

  const onSignInSubmit = useCallback(
    async (payload: { email: string; password: string }) => {
      setServerError(null)
      try {
        await onSignIn(payload)
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 401) {
          setServerError('errors.invalidEmailOrPassword')
        } else {
          setServerError('errors.unableToSignIn')
        }
      }
    },
    [onSignIn]
  )

  return (
    <AuthLayout
      productBadge={authLayoutBadge}
      productSubtitle={authLayoutSubtitle}
      hideInterfaceLanguageSelect={hideInterfaceLanguageSelect}
    >
      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="text-lg tracking-tight">
            {t('actions.signIn')}
          </CardTitle>
          <CardDescription>
            {t('description')}
            {showSignUpLink ? (
              <>
                <br />
                {t('signUpPrompt')}{' '}
                <Link
                  to={signUpPath}
                  className="underline underline-offset-4 hover:text-primary"
                >
                  {t('signUpLink')}
                </Link>
              </>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <WeChatAuthErrorAlert errorCode={wechatError} translate={t} />
          {serverError && (
            <Alert variant="destructive">
              <AlertDescription>{serverError ? t(serverError) : null}</AlertDescription>
            </Alert>
          )}
          {showWeChatAuth ? (
            <WeChatQrLoginButton label={t('wechat.continueWithWeChat')} />
          ) : null}
          {showWeChatAuth ? (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {t('wechat.orContinueWithEmail')}
                </span>
              </div>
            </div>
          ) : null}
          <UserAuthForm onSignIn={onSignInSubmit} isSigningIn={isSigningIn} />
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
